import {isFeatureEnabled} from '@github-ui/feature-flags'
import {SPECIAL_VALUES} from '@github-ui/item-picker-base/constants/placeholders'
import {VALUES} from '@github-ui/item-picker-base/constants/values'
import {ItemPicker, type ExtendedItemProps} from '@github-ui/item-picker-base/ItemPicker'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useDebounce} from '@github-ui/use-debounce'
import {useCallback, useDeferredValue, useMemo, useRef, useState} from 'react'
import type {SelectPanelItemProps as ItemProps} from '@primer/react'
import {MilestoneIcon, PlusIcon} from '@primer/octicons-react'
import {MilestoneDescription} from './MilestoneDescription'
import type {ItemGroup} from '@github-ui/item-picker-base/shared'
import {LABELS} from '@github-ui/item-picker-base/constants/labels'
import {ERRORS} from '@github-ui/item-picker-base/constants/errors'
import {hasFuzzyMatch} from '@github-ui/fuzzy-score/has-fuzzy-match'
import type {BaseItemPickerProps} from '@github-ui/item-picker-base/base-item-picker-props'

import styles from './ItemPickerMilestone.module.css'

export type MilestonePickerMilestone = {
  id: string
  title: string
  closed: boolean
  closedAt: string | null | undefined
  dueOn: string | null | undefined
  progressPercentage: number
  url: string
}

export type MilestonePickerBaseProps = Omit<BaseItemPickerProps<MilestonePickerMilestone>, 'filterItems'> & {
  repo: string
  owner: string
  activeMilestone: MilestonePickerMilestone | null
  canCreateMilestone: boolean
  noMilestoneItem?: ExtendedItemProps<MilestonePickerMilestone>
  showMilestoneDescription?: boolean
  showNoMatchItem?: boolean
  repositoryId: string

  milestones: MilestonePickerMilestone[]
  setIsLoading: (isLoading: boolean) => void

  fetchMilestones: (options: {
    owner: string
    repo: string
    query: string
    count?: number
  }) => Promise<MilestonePickerMilestone[]>
  onCreate: CreateMilestoneMutationCallback
}

const openGroup: ItemGroup = {groupId: 'open', header: {title: 'Open', variant: 'filled'}}
const closedGroup: ItemGroup = {groupId: 'closed', header: {title: 'Closed', variant: 'filled'}}

function compareMilestones(a: MilestonePickerMilestone, b: MilestonePickerMilestone): number {
  // Open milestones: sort by dueOn ascending (nearest deadline first)
  if (!a.closed && !b.closed) {
    if (a.dueOn && b.dueOn) return new Date(a.dueOn).getTime() - new Date(b.dueOn).getTime()
    if (a.dueOn) return -1
    if (b.dueOn) return 1
    return a.title.localeCompare(b.title)
  }
  // Closed milestones: sort by closedAt descending (most recently closed first)
  if (a.closed && b.closed) {
    if (a.closedAt && b.closedAt) return new Date(b.closedAt).getTime() - new Date(a.closedAt).getTime()
    if (a.closedAt) return -1
    if (b.closedAt) return 1
    return a.title.localeCompare(b.title)
  }
  // Open before closed
  return a.closed ? 1 : -1
}

export type MutationCallback<TVariables, TResponse> = (
  variables: TVariables,
  options: {onSuccess: (response: TResponse | null) => void; onError: () => void},
) => void
export type CreateMilestoneVariables = {repositoryId: string; title: string}
export type CreateMilestoneMutationCallback = MutationCallback<CreateMilestoneVariables, MilestonePickerMilestone>

export function MilestonePickerBase({
  repo,
  owner,
  milestones,
  repositoryId,
  activeMilestone,
  onSelectionChange,
  insidePortal,
  anchorElement,
  nested,
  triggerOpen,
  initialFilter,
  isLoading,
  setIsLoading,
  noMilestoneItem,
  showMilestoneDescription,
  title,
  canCreateMilestone,
  fetchMilestones,
  onCreate,
  showNoMatchItem = false,
}: MilestonePickerBaseProps) {
  const [filter, setFilter] = useState('')
  const deferredFilter = useDeferredValue(filter)
  const [isServerFetching, setIsServerFetching] = useState(false)
  const [searchedMilestones, setSearchedMilestones] = useState<MilestonePickerMilestone[] | undefined>()
  const [loadedMilestones, setLoadedMilestones] = useState<Array<MilestonePickerMilestone & {__isNew__?: boolean}>>([])
  const {addToast} = useToastContext()

  const items = useMemo(() => {
    if (searchedMilestones !== undefined && searchedMilestones.length > 0) return searchedMilestones

    const repositoryMilestones = milestones.slice().sort(compareMilestones).concat(loadedMilestones)

    if (noMilestoneItem) repositoryMilestones.unshift(SPECIAL_VALUES.noMilestoneData as MilestonePickerMilestone)
    if (!deferredFilter) return repositoryMilestones
    return repositoryMilestones.filter(l => l.title.toLowerCase().indexOf(deferredFilter.toLowerCase()) >= 0)
  }, [milestones, deferredFilter, loadedMilestones, noMilestoneItem, searchedMilestones])

  const isNoMilestoneSelected = noMilestoneItem && noMilestoneItem.selected

  const openItems = useMemo(() => {
    if (noMilestoneItem) {
      return items.filter(m => m.closed === false)
    } else {
      return []
    }
  }, [noMilestoneItem, items])

  const searchMilestones = useCallback(
    async (query: string) => {
      setIsServerFetching(true)
      setFilter(query)

      try {
        const fetchedData = await fetchMilestones({query, owner, repo, count: 10})

        if (fetchedData !== undefined) {
          if (noMilestoneItem && hasFuzzyMatch(query, noMilestoneItem.source?.title ?? '')) {
            fetchedData.unshift(SPECIAL_VALUES.noMilestoneData as MilestonePickerMilestone)
          }

          setSearchedMilestones(fetchedData)
        }
      } catch {
        // Temporarily disabling this until we have a standard way of displaying fetch errors
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: ERRORS.couldNotSearchMilestones,
        })
      } finally {
        setIsServerFetching(false)
      }
    },
    [addToast, fetchMilestones, noMilestoneItem, owner, repo],
  )

  const debounceFetchSearchData = useDebounce(
    (nextValue: string) => searchMilestones(nextValue),
    VALUES.pickerDebounceTime,
  )

  const filterItems = useCallback(
    async (value: string) => {
      const trimmedFilter = value.trim()
      if (filter === trimmedFilter) {
        return
      }

      if (trimmedFilter === '') {
        setSearchedMilestones(undefined)
        setFilter(trimmedFilter)
        return
      }

      debounceFetchSearchData(trimmedFilter)
    },
    [debounceFetchSearchData, filter],
  )

  const getItemKey = useCallback((milestone: MilestonePickerMilestone) => milestone.id, [])

  const createNewMilestone = useCallback(
    (
      _: ItemProps,
      milestone: MilestonePickerMilestone,
      event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
    ) => {
      event.preventDefault()
      event.stopPropagation()

      if (!repositoryId) return
      setIsLoading(true)

      onCreate(
        {
          repositoryId,
          title: milestone.title,
        },
        {
          onError: () => {
            // Temporarily disabling this until we have a standard way of displaying fetch errors
            // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
            addToast({
              type: 'error',
              message: ERRORS.couldNotCreateMilestone,
            })
            setIsLoading(false)
          },
          onSuccess: newMilestone => {
            if (newMilestone) {
              setLoadedMilestones([{...newMilestone, __isNew__: true}, ...loadedMilestones])
            }
            setIsLoading(false)
          },
        },
      )
    },
    [addToast, loadedMilestones, onCreate, repositoryId, setIsLoading],
  )

  const convertToItemProps = useCallback(
    (milestone: MilestonePickerMilestone): ExtendedItemProps<MilestonePickerMilestone> => {
      if (noMilestoneItem && milestone.id === SPECIAL_VALUES.noMilestoneData.id) {
        return {...noMilestoneItem}
      }

      if (milestone.id === filter) {
        return {
          id: milestone.id,
          text: `Create new milestone: "${milestone.title}"`,
          source: milestone,
          trailingVisual: PlusIcon,
          className: styles.MilestoneFilterItem,
          onAction: (item: ItemProps, event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) =>
            createNewMilestone(item, milestone, event),
        }
      }

      const {closed, closedAt, progressPercentage, dueOn} = milestone

      return {
        id: milestone.id,
        text: milestone.title,
        description: showMilestoneDescription
          ? ((
              <MilestoneDescription
                closed={closed}
                closedAt={closedAt}
                progressPercentage={progressPercentage}
                dueOn={dueOn}
                showProgressPercentage={false}
              />
            ) as unknown as string)
          : undefined,
        descriptionVariant: 'block',
        className: styles.MilestoneItem,
        source: milestone,
        groupId: milestone.closed ? closedGroup.groupId : openGroup.groupId,
        leadingVisual: () => <MilestoneIcon size={16} />,
      }
    },
    [createNewMilestone, filter, noMilestoneItem, showMilestoneDescription],
  )

  // Only show groups for which the filter returns at least one item
  const groups: ItemGroup[] = useMemo(() => {
    const itemGroups = []
    if (items.some(i => !i.closed)) {
      itemGroups.push(openGroup)
    }
    if (items.some(i => i.closed)) {
      itemGroups.push(closedGroup)
    }
    return itemGroups
  }, [items])

  if (noMilestoneItem && isNoMilestoneSelected) {
    activeMilestone = SPECIAL_VALUES.noMilestoneData as MilestonePickerMilestone
  }

  const anchorRef = useRef<HTMLButtonElement>(null)
  const initialSelectedItems = useMemo(() => {
    return activeMilestone ? [activeMilestone] : []
  }, [activeMilestone])

  const showNoMatchMessage = useMemo<boolean>(() => {
    const hasNoItems = !isServerFetching && !filter && items.length === 0
    const hasNoFilterResults = !(showNoMatchItem && canCreateMilestone) && !!filter && items.length === 0
    return hasNoItems || hasNoFilterResults
  }, [filter, isServerFetching, items.length, showNoMatchItem, canCreateMilestone])

  const responsiveOnNarrowEnabled = isFeatureEnabled('issues_react_select_panel_fullscreen_on_narrow')

  return (
    <ItemPicker
      loading={isLoading || isServerFetching}
      items={noMilestoneItem ? openItems : items}
      initialSelectedItems={initialSelectedItems}
      filterItems={filterItems}
      title={title ?? LABELS.milestonesHeader}
      getItemKey={getItemKey}
      convertToItemProps={convertToItemProps}
      placeholderText={`Filter milestones ${
        milestones.length >= VALUES.milestonesMaxPreloadCount
          ? `(${VALUES.milestonesMaxPreloadCount} milestones loaded)`
          : ''
      }`}
      selectionVariant="single"
      onSelectionChange={onSelectionChange}
      groups={noMilestoneItem ? undefined : groups}
      renderAnchor={props => anchorElement(props, anchorRef)}
      insidePortal={insidePortal}
      height={'large'}
      width={'medium'}
      nested={nested}
      resultListAriaLabel={'Milestone results'}
      triggerOpen={triggerOpen}
      initialFilter={initialFilter}
      selectPanelRef={anchorRef}
      keybindingCommandId="item-pickers:open-milestone"
      customNoMatchItem={
        canCreateMilestone && showNoMatchItem && filter
          ? ({
              id: filter,
              closed: false,
              progressPercentage: 0,
              title: filter,
              url: '',
            } as MilestonePickerMilestone)
          : undefined
      }
      improvedNoMatchAccessibility={showNoMatchMessage}
      noMatchMessage={
        showNoMatchMessage
          ? {
              title: 'No milestones were found',
              body: 'Try searching with a different query for results.',
              variant: 'empty',
            }
          : undefined
      }
      responsiveOnNarrow={responsiveOnNarrowEnabled}
    />
  )
}
